import { joinSurveyAction } from '../join-survey';
import type { ConnectChannel } from '../types';

/**
 * Static content for the Purchase channel detail page (`/connect/purchase`).
 * Same sourcing rule as demographics.ts: copied, not imported, since the
 * source (Data Catalog / EXPLORE_CHANNELS) backs internal-only tooling and
 * this page is visible to every Home user.
 */
export const PURCHASE_CHANNEL: ConnectChannel = {
    name: 'Purchase',
    icon: 'receipt',
    color: '#418363',
    subtitle: 'US · Amazon, Gmail receipts, in-store',
    subtitleLine2: 'Daily · Since 2020',
    exploreDaniPrompt: 'What can purchase receipt data tell me about my consumers?',
    properties: [
        {
            name: 'Amazon',
            letter: 'a',
            color: '#F0A93F',
            icon: 'simple-icons:amazon',
            iconColor: '#FF9900',
        },
        { name: 'Gmail', letter: 'M', color: '#EA4335', icon: 'logos:google-gmail' },
    ],
    actions: [
        {
            id: 'purchase-baskets',
            name: 'Analyze baskets',
            icon: 'shopping-cart',
            summary: 'what people buy alongside your brand, and what they pay',
            template:
                'What ends up in the basket alongside {brand|my brand}, and at what price, over {window|the last 12 weeks}?',
        },
        {
            id: 'purchase-leakage',
            name: 'Find leakage',
            icon: 'trending-up',
            summary: 'where your brand is losing basket share, and to whom',
            template:
                'Where is {brand|my brand} losing basket share to {competitors|competitors} over {window|the last 12 weeks}?',
        },
        {
            id: 'purchase-wallet',
            name: 'Size wallet share',
            icon: 'pie-chart',
            summary: "how much of your category's spending your brand captures next to competitors",
            template:
                'What share of wallet does {brand|my brand} capture in {category|my category} vs. {competitors|competitors} over {window|the last 12 weeks}?',
        },
        {
            id: 'purchase-price',
            name: 'Check price paid',
            icon: 'tag',
            summary: 'what consumers actually pay for your products',
            template:
                'What price are consumers actually paying for {brand|my} products over {window|the last 12 weeks}?',
        },
        joinSurveyAction('purchase'),
    ],
    schema: [
        { field: 'consumer_id', type: 'string', description: 'Pseudonymous panel ID' },
        { field: 'merchant', type: 'string', description: 'Store / retailer' },
        { field: 'item_names', type: 'string[]', description: 'Line items (when parsed)' },
        { field: 'total', type: 'decimal', description: 'Order total in USD' },
        { field: 'item_count', type: 'integer', description: 'Number of line items' },
        { field: 'channel', type: 'string', description: 'Online / In-Store' },
        { field: 'source', type: 'string', description: 'amazon / gmail' },
        { field: 'purchase_date', type: 'date', description: 'Order date' },
    ],
    sampleCols: ['Merchant', 'Items', 'Total', 'Channel', 'Source', 'Date'],
    sampleColWidths: ['minmax(90px, 1fr)', 'minmax(200px, 3fr)', '70px', '80px', '80px', '90px'],
    sampleRows: [
        ['Amazon', 'Little Debbie Oatmeal Crème Pies', '$10.72', '—', 'amazon', '2026-04-24'],
        ['Walmart', '—', '$72.35', 'Online', 'gmail', '2026-04-05'],
        ['Amazon', 'Amazon Music Unlimited', '$11.99', 'Online', 'amazon', '2026-04-24'],
        ['Lyft', 'Standard fare (3.01mi, 13m 26s)', '$14.94', 'Online', 'gmail', '2026-04-04'],
        ['Amazon', '—', '$50.98', 'In-Store', 'amazon', '2026-04-24'],
    ],
};
